import { useState } from 'react';
import { Sparkles, Loader2, Plus } from 'lucide-react';
import { generateWithAI } from '../lib/api';
import type { ResumeData } from '../types/resume';

interface AddSkillsWithAIProps {
  resume: ResumeData;
  onAdd: (skills: string[]) => void;
}

export function AddSkillsWithAI({ resume, onAdd }: AddSkillsWithAIProps) {
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleGenerate = async () => {
    setLoading(true);
    setError(null);
    try {
      const { result } = await generateWithAI({ type: 'skills', resume, context: '', prompt: 'Suggest relevant technical and soft skills based on this resume.' });
      const list = Array.isArray(result) ? result : [];
      const existing = new Set(resume.skills.map((s) => s.name.toLowerCase()));
      setSuggestions(list.filter((s) => !existing.has(s.toLowerCase())));
    } catch (err: any) {
      setError(err.message || 'Failed to suggest skills');
    } finally {
      setLoading(false);
    }
  };

  const addSkill = (name: string) => {
    onAdd([name]);
    setSuggestions((prev) => prev.filter((s) => s !== name));
  };

  const addAll = () => {
    onAdd(suggestions);
    setSuggestions([]);
  };

  return (
    <div className="rounded-xl border border-primary-100 bg-primary-50/50 p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary-600" />
          <span className="text-sm font-medium text-slate-900">Add Skills with AI</span>
        </div>
        <button
          onClick={handleGenerate}
          disabled={loading}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary-700 hover:text-primary-800 bg-white border border-primary-200 px-2.5 py-1.5 rounded-lg transition-colors disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
          {loading ? 'Suggesting...' : suggestions.length ? 'Refresh' : 'Suggest'}
        </button>
      </div>

      {error && <div className="text-xs text-red-600 bg-red-50 rounded-lg p-2">{error}</div>}

      {suggestions.length > 0 && (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((skill) => (
              <button
                key={skill}
                onClick={() => addSkill(skill)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs bg-white text-primary-700 border border-primary-200 hover:bg-primary-100 transition-colors"
              >
                {skill}
                <Plus className="h-3 w-3" />
              </button>
            ))}
          </div>
          <button
            onClick={addAll}
            className="text-xs font-medium text-primary-700 hover:text-primary-800"
          >
            Add all suggestions
          </button>
        </div>
      )}

      {!suggestions.length && !loading && !error && (
        <p className="text-xs text-slate-500">
          AI will suggest skills based on your experience and target role.
        </p>
      )}
    </div>
  );
}
